<template>
  <div class="sp">
    <div class="sp-brew" :style="{ width: scrollProgress + '%' }"></div>

    <transition name="sp-modal-fade">
      <div v-if="showSuccessModal" class="sp-modal-overlay" @click.self="closeSuccessModal">
        <div class="sp-modal" role="dialog" aria-modal="true" aria-label="Subscription confirmed">
          <div class="sp-modal-kicker">You're on the list</div>
          <h3 class="sp-modal-title">Wise choice<br /><em>well brewed.</em></h3>
          <p class="sp-modal-text">Check your inbox for our welcome email!</p>
          <button class="sp-go sp-modal-close" type="button" @click="closeSuccessModal">
            Close
          </button>
        </div>
      </div>
    </transition>

    <header class="sp-nav" :class="{ 'sp-scrolled': scrolled }">
      <a class="sp-lockup" @click="$router.push('/')">
        <img src="../assets/tcb-cup.png" alt="The Coffee Break cup logo" />
        <span>The Coffee Break</span>
      </a>
      <div class="sp-nav-actions">
        <div class="sp-menu-wrap" ref="menuWrap">
          <button
            class="sp-menu-btn"
            @click="menuOpen = !menuOpen"
            :aria-expanded="menuOpen"
          >
            Menu
          </button>
          <div class="sp-menu-dropdown" :class="{ 'sp-menu-open': menuOpen }">
            <a @click="goto('/about')">About</a>
            <a @click="goto('/career')">Career</a>
            <a @click="goto('/ilovetcb')">
              <svg class="sp-heart" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                ></path>
              </svg>
              Support Us
            </a>
            <a @click="onManageAccount">Manage Account</a>
          </div>
        </div>
        <button class="sp-cta" @click="focusFinalEmail">Subscribe — Free</button>
      </div>
    </header>

    <main>
      <section class="sp-hero">
        <div class="sp-wrap sp-hero-wrap">
          <div class="sp-issue">Weekday mornings</div>

          <h1 class="sp-display">
            <span class="sp-row"><span>Big news.</span></span>
            <span class="sp-row"><span><em class="sp-cupword">Small cup.</em></span></span>
            <span class="sp-row"><span>3 minutes.</span></span>
          </h1>

          <div class="sp-hero-foot">
            <div class="sp-hero-copy">
              <p class="sp-sub">
                The daily brief <b>&gt;24,000 Malaysians</b> read before their first sip — the news
                distilled into one sharp, ad-free email. Free, forever.
              </p>
            </div>
            <div class="sp-hero-cta">
              <form class="sp-capture" @submit.prevent="subscribeHero">
                <div class="sp-fields">
                  <div class="sp-field">
                    <label class="sp-field-label" for="sp-hero-email">Email</label>
                    <input
                      id="sp-hero-email"
                      v-model="heroEmail"
                      type="email"
                      required
                      placeholder="you@email.com"
                      aria-label="Email address"
                    />
                  </div>
                  <div class="sp-field sp-field--optional">
                    <label class="sp-field-label" for="sp-hero-referral">
                      Referral code <span class="sp-field-opt">— optional</span>
                    </label>
                    <input
                      id="sp-hero-referral"
                      v-model="referral"
                      placeholder="Referral code"
                      aria-label="Referral code"
                    />
                  </div>
                </div>
                <button
                  class="sp-go sp-go--block"
                  type="submit"
                  :class="{ 'sp-go--ok': heroState === 'done' }"
                >
                  <template v-if="heroState === 'brewing'">Brewing… ☕</template>
                  <template v-else-if="heroState === 'done'">You're in ✓</template>
                  <template v-else>Subscribe — it's free <span class="sp-arr">→</span></template>
                </button>
                <div ref="turnstileHero" class="sp-turnstile"></div>
              </form>
            </div>
          </div>

          <div class="sp-hero-art" :style="{ transform: 'translateY(' + heroParallax + 'px)' }">
            <div class="sp-steam" aria-hidden="true">
              <span class="sp-puff sp-p1"></span>
              <span class="sp-puff sp-p2"></span>
              <span class="sp-puff sp-p3"></span>
              <span class="sp-puff sp-p4"></span>
              <span class="sp-puff sp-p5"></span>
            </div>
            <img
              src="../assets/mascot-chair-mask.png"
              alt="The Coffee Break mascot wearing a haze mask, reading a newspaper in a lounge chair"
            />
          </div>
        </div>

        <div class="sp-ticker">
          <div class="sp-track">
            <span class="sp-item" v-for="(item, i) in tickerLoop" :key="i">{{ item }}</span>
          </div>
        </div>
      </section>

      <section class="sp-latest" v-if="latestLoading || latestIssues.length">
        <div class="sp-wrap">
          <div class="sp-kicker" v-reveal><span class="sp-live-dot"></span>Fresh off the press</div>
          <h2 v-reveal>Today's issue,<br /><em>hot off the wire.</em></h2>

          <div class="sp-latest-rows">
            <template v-if="latestLoading">
              <div class="sp-latest-row sp-latest-row--skeleton" v-for="n in 5" :key="n">
                <span class="sp-skel sp-skel--date"></span>
                <span class="sp-skel sp-skel--title"></span>
              </div>
            </template>
            <template v-else>
              <a
                class="sp-latest-row"
                v-reveal
                v-for="issue in latestIssues"
                :key="issue.guid"
                :href="issue.link"
                target="_blank"
                rel="noopener"
              >
                <span class="sp-latest-date">{{ issue.dateLabel }}</span>
                <span class="sp-latest-title">{{ issue.title }}</span>
                <span class="sp-latest-teaser">{{ issue.teaser }}</span>
                <span class="sp-latest-arrow">→</span>
              </a>
            </template>
          </div>
        </div>
      </section>

      <section class="sp-anatomy">
        <div class="sp-wrap">
          <div class="sp-kicker" v-reveal>The anatomy of an issue</div>
          <h2 v-reveal>Everything that matters,<br /><em>nothing that doesn't.</em></h2>
          <div class="sp-rows">
            <div class="sp-rowitem" v-reveal v-for="row in anatomyRows" :key="row.num">
              <span class="sp-num">{{ row.num }}</span>
              <span class="sp-name">{{ row.name }}</span>
              <span class="sp-desc">{{ row.desc }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="sp-stats">
        <div class="sp-wrap">
          <div class="sp-grid">
            <div class="sp-cell" v-reveal v-for="stat in stats" :key="stat.k">
              <span class="sp-v" v-html="stat.v"></span>
              <span class="sp-k">{{ stat.k }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="sp-dark">
        <div class="sp-wrap">
          <div class="sp-kicker" v-reveal>Readers say</div>
          <div class="sp-quote-body" :class="{ 'sp-swap': swapping }">
            <blockquote class="sp-bigquote" v-reveal v-html="quotes[quoteIndex].t"></blockquote>
            <div class="sp-attrib">{{ quotes[quoteIndex].a }}</div>
          </div>
          <div class="sp-quote-nav">
            <button aria-label="Previous quote" @click="cycleQuote(-1)">←</button>
            <button aria-label="Next quote" @click="cycleQuote(1)">→</button>
          </div>
        </div>
      </section>

      <section class="sp-final">
        <div class="sp-sun"></div>
        <div class="sp-wrap">
          <div class="sp-kicker sp-kicker--center" v-reveal>One email. Every weekday.</div>
          <h2 v-reveal>Wake up<br /><em>already caught up.</em></h2>
          <p v-reveal>Join &gt;24,000 Malaysians. Unsubscribe anytime — no hard feelings.</p>
          <form class="sp-capture sp-capture--center" v-reveal @submit.prevent="subscribeFinal">
            <input
              ref="finalEmail"
              v-model="finalEmail"
              type="email"
              required
              placeholder="you@email.com"
              aria-label="Email address"
            />
            <button class="sp-go" type="submit" :class="{ 'sp-go--ok': finalState === 'done' }">
              <template v-if="finalState === 'brewing'">Brewing… ☕</template>
              <template v-else-if="finalState === 'done'">You're in ✓</template>
              <template v-else>Subscribe <span class="sp-arr">→</span></template>
            </button>
          </form>
          <div class="sp-referral sp-referral--center sp-referral--after" v-reveal>
            <span class="sp-referral-label">Referral code</span>
            <input
              v-model="referral"
              class="sp-referral-input"
              placeholder="Referral code"
              aria-label="Referral code"
            />
          </div>
          <div ref="turnstileFinal" class="sp-turnstile sp-turnstile--center"></div>
          <div class="sp-note" v-reveal>Free forever · No spam · 3-minute read</div>
        </div>
        <footer>
          <span>Copyright © 2023 FINLIT MEDIA TECH GROUP SDN BHD (1348643-U)</span>
          <span>Brewed in Kuala&nbsp;Lumpur ☕</span>
          <span>
            <a @click="$router.push('/about')">About</a> ·
            <a href="https://thecoffeebreak.substack.com/archive" target="_blank" rel="noopener">Archive</a> ·
            <a @click="$router.push('/ilovetcb')">Support us</a> ·
            <a @click="$router.push('/privacy-policy')">Privacy Policy</a> ·
            <a @click="$router.push('/terms-of-service')">Terms of Service</a>
          </span>
        </footer>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { renderTurnstile, resetTurnstile } from "../turnstile";

const SUBSCRIBE_URL =
  "https://hukopzhug3.execute-api.ap-southeast-1.amazonaws.com/production/email/submit";

export default {
  name: "ShowpieceLanding",
  directives: {
    reveal: {
      inserted(el) {
        el.classList.add("sp-reveal");
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          el.classList.add("sp-in");
          return;
        }
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                el.classList.add("sp-in");
                io.unobserve(el);
              }
            });
          },
          { threshold: 0.15 }
        );
        io.observe(el);
      },
    },
  },
  data() {
    return {
      turnstileHeroToken: "",
      turnstileFinalToken: "",
      turnstileHeroId: null,
      turnstileFinalId: null,
      menuOpen: false,
      scrolled: false,
      scrollProgress: 0,
      heroParallax: 0,
      heroEmail: "",
      heroState: "idle",
      finalEmail: "",
      finalState: "idle",
      showSuccessModal: false,
      referral: "",
      quoteIndex: 0,
      swapping: false,
      latestIssues: [],
      latestLoading: true,
      tickerItems: [
        "Bursa opens higher on tech rally",
        "Ringgit holds firm against the dollar",
        "New MRT line cuts KL commutes by half",
        "Budget 2027: what it means for your wallet",
        "Malaysian startup lands Series B",
        "Durian exports hit record season",
      ],
      anatomyRows: [
        {
          num: "01",
          name: "Malaysia",
          desc: "The stories shaping the country — policy, people, and the occasional plot twist.",
        },
        {
          num: "02",
          name: "Markets",
          desc: "Bursa, ringgit, and your money — explained like a friend would, not a terminal.",
        },
        {
          num: "03",
          name: "World",
          desc: "The global headlines worth your attention, minus the doomscroll.",
        },
        {
          num: "04",
          name: "Tech & Culture",
          desc: "What's new, what's next, and what everyone will be talking about at lunch.",
        },
      ],
      stats: [
        { v: "<b>20k</b>+", k: "Daily readers" },
        { v: "3<b>min</b>", k: "Average read" },
        { v: "5<b>×</b>", k: "Issues a week" },
        { v: "RM<b>0</b>", k: "Forever." },
      ],
      quotes: [
        {
          t: '"The only newsletter I actually <em>finish</em>. It\'s become as much a part of my morning as the coffee itself."',
          a: "— Aisha · Reader since 2021",
        },
        {
          t: '"I cancelled two news subscriptions after finding this. Three minutes and I\'m <em>done</em> — genuinely done."',
          a: "— Daniel · Reader since 2022",
        },
        {
          t: '"Smart without being smug. It\'s the group chat\'s <em>source of truth</em> every morning."',
          a: "— Mei Lin · Reader since 2020",
        },
      ],
    };
  },
  computed: {
    tickerLoop() {
      return this.tickerItems.concat(this.tickerItems);
    },
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    this.referral = urlParams.get("referral") || urlParams.get("ref") || "";
    window.addEventListener("scroll", this.onScroll, { passive: true });
    document.addEventListener("click", this.onDocClick);
    this.retrieveLatest();
    this.$nextTick(async () => {
      this.turnstileHeroId = await renderTurnstile(
        this.$refs.turnstileHero,
        (token) => { this.turnstileHeroToken = token; }
      );
      this.turnstileFinalId = await renderTurnstile(
        this.$refs.turnstileFinal,
        (token) => { this.turnstileFinalToken = token; }
      );
    });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("click", this.onDocClick);
  },
  methods: {
    onScroll() {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      this.scrollProgress = max > 0 ? (h.scrollTop / max) * 100 : 0;
      this.scrolled = h.scrollTop > 40;
      this.heroParallax = Math.min(h.scrollTop * 0.12, 90);
    },
    onDocClick(e) {
      if (this.menuOpen && this.$refs.menuWrap && !this.$refs.menuWrap.contains(e.target)) {
        this.menuOpen = false;
      }
    },
    goto(path) {
      this.menuOpen = false;
      this.$router.push(path);
    },
    onManageAccount() {
      this.menuOpen = false;
      window.open("https://billing.stripe.com/p/login/14k5mT1L4aFi6TC8ww", "_blank", "noreferrer");
    },
    focusFinalEmail() {
      this.$refs.finalEmail.scrollIntoView({ behavior: "smooth", block: "center" });
      this.$refs.finalEmail.focus();
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    async retrieveLatest() {
      try {
        const { data } = await axios.get(
          "https://hukopzhug3.execute-api.ap-southeast-1.amazonaws.com/production/retrieve-top-feeds"
        );
        this.latestIssues = (data.result || []).slice(0, 5).map((item) => {
          const teaser = (item.content || "").split(".")[0].trim();
          return {
            guid: item.guid,
            link: item.link,
            title: item.title.replace(/^☕️?\s*/, ""),
            teaser: teaser ? `${teaser}.` : "",
            dateLabel: new Intl.DateTimeFormat("en-MY", {
              day: "numeric",
              month: "short",
            }).format(new Date(item.date)),
          };
        });
      } catch {
        this.latestIssues = [];
      } finally {
        this.latestLoading = false;
      }
    },
    cycleQuote(dir) {
      this.swapping = true;
      setTimeout(() => {
        this.quoteIndex = (this.quoteIndex + dir + this.quotes.length) % this.quotes.length;
        this.swapping = false;
      }, 350);
    },
    async subscribeHero() {
      await this._subscribe("hero");
    },
    async subscribeFinal() {
      await this._subscribe("final");
    },
    async _subscribe(source) {
      const isHero = source === "hero";
      const emailVal = (isHero ? this.heroEmail : this.finalEmail).trim();
      if (!emailVal) return;

      if (isHero) this.heroState = "brewing";
      else this.finalState = "brewing";

      try {
        this.$gtag.event("subscribe", {
          event_label: "subscribe_showpiece",
          value: emailVal,
        });
        await axios.post(SUBSCRIBE_URL, {
          email: emailVal,
          referral: this.referral.trim(),
          token: isHero ? this.turnstileHeroToken : this.turnstileFinalToken,
        });
        this._resetTurnstile(isHero);
        this.showSuccessModal = true;
        if (isHero) {
          this.heroState = "done";
          this.heroEmail = "";
        } else {
          this.finalState = "done";
          this.finalEmail = "";
        }
      } catch (err) {
        this._resetTurnstile(isHero);
        if (isHero) this.heroState = "idle";
        else this.finalState = "idle";
      }
    },
    _resetTurnstile(isHero) {
      if (isHero) {
        resetTurnstile(this.turnstileHeroId);
        this.turnstileHeroToken = "";
      } else {
        resetTurnstile(this.turnstileFinalId);
        this.turnstileFinalToken = "";
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=Instrument+Serif:ital@0;1&family=Roboto+Mono:wght@400;500&display=swap");
</style>

<style scoped>
.sp {
  --paper: #faf7f0;
  --paper-2: #f3eee2;
  --ink: #101820;
  --ink-2: #3d4852;
  --ink-3: #7a8691;
  --blue: #007cb7;
  --blue-deep: #083d73;
  --sky: #e3f2fd;
  --volt: #ffdb00;
  --line: rgba(16, 24, 32, 0.14);
  --serif: "Instrument Serif", Georgia, serif;
  --grotesk: "Bricolage Grotesque", "Helvetica Neue", sans-serif;
  --mono: "Roboto Mono", Menlo, monospace;
  --ease: cubic-bezier(0.16, 1, 0.3, 1);

  font-family: var(--grotesk);
  background: var(--paper);
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  position: relative;
}
.sp *,
.sp *::before,
.sp *::after {
  box-sizing: border-box;
}
.sp ::selection {
  background: var(--volt);
  color: var(--ink);
}
.sp::after {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 60;
  opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");
}

/* brew bar */
.sp-brew {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: var(--blue);
  z-index: 65;
}

/* nav */
.sp-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
  transition: background 0.3s, box-shadow 0.3s;
}
.sp-scrolled {
  background: rgba(250, 247, 240, 0.92);
  backdrop-filter: blur(12px);
  box-shadow: 0 1px 0 var(--line);
}
.sp-lockup {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--ink);
  cursor: pointer;
}
.sp-lockup img {
  height: 30px;
  display: block;
}
.sp-lockup span {
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -0.02em;
}
@keyframes sp-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
.sp-nav-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}
.sp-menu-wrap {
  position: relative;
}
.sp-menu-btn {
  font-family: var(--grotesk);
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.02em;
  color: var(--ink);
  background: transparent;
  border: 1.5px solid var(--line);
  border-radius: 999px;
  padding: 10.5px 20px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.sp-menu-btn:hover {
  border-color: var(--blue);
  background: rgba(0, 124, 183, 0.06);
}
.sp-menu-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 200px;
  background: var(--paper);
  border: 1.5px solid var(--line);
  border-radius: 16px;
  padding: 10px;
  box-shadow: 0 20px 40px rgba(16, 24, 32, 0.14);
  display: none;
  flex-direction: column;
}
.sp-menu-dropdown.sp-menu-open {
  display: flex;
}
.sp-menu-dropdown a {
  font-family: var(--grotesk);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.sp-menu-dropdown a:hover {
  background: var(--sky);
  color: var(--blue);
}
.sp-heart {
  color: #e5484d;
  vertical-align: -2px;
  margin-right: 4px;
}
/* desktop: list the links inline instead of behind the Menu button */
@media (min-width: 961px) {
  .sp-menu-btn {
    display: none;
  }
  .sp-menu-dropdown {
    position: static;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;
    min-width: 0;
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
  }
  .sp-menu-dropdown a {
    white-space: nowrap;
  }
}
.sp-cta {
  font-family: var(--grotesk);
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.02em;
  color: var(--paper);
  background: var(--ink);
  border: none;
  border-radius: 999px;
  padding: 12px 22px;
  cursor: pointer;
  transition: transform 0.3s var(--ease), background 0.3s;
}
.sp-cta:hover {
  background: var(--blue);
  transform: scale(1.05);
}
@media (max-width: 720px) {
  .sp-nav-actions {
    gap: 8px;
  }
  .sp-menu-btn {
    padding: 9px 14px;
    font-size: 12px;
  }
  .sp-cta {
    padding: 10px 16px;
    font-size: 12px;
  }
  .sp-nav {
    padding: 14px 18px;
  }
}
@media (max-width: 420px) {
  .sp-lockup span {
    display: none;
  }
}

/* hero */
.sp-hero {
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 120px 32px 0;
  position: relative;
}
.sp-hero-wrap {
  width: 100%;
  max-width: 1400px;
  position: relative;
  margin: 0 auto;
}
.sp-issue {
  font-family: var(--mono);
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
}
.sp-issue::before {
  content: "";
  width: 44px;
  height: 1px;
  background: var(--ink);
  display: inline-block;
}
.sp-display {
  font-weight: 800;
  font-size: clamp(58px, 11.6vw, 178px);
  line-height: 0.92;
  letter-spacing: -0.04em;
  margin: 0;
  text-transform: uppercase;
}
.sp-row {
  display: block;
  overflow: hidden;
}
.sp-row > span {
  display: inline-block;
  transform: translateY(110%);
  animation: sp-rise 1.1s var(--ease) forwards;
}
.sp-row:nth-child(2) > span {
  animation-delay: 0.12s;
}
.sp-row:nth-child(3) > span {
  animation-delay: 0.24s;
}
@keyframes sp-rise {
  to {
    transform: translateY(0);
  }
}
.sp-display em {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  text-transform: none;
  letter-spacing: -0.01em;
  color: var(--blue);
}
.sp-cupword {
  position: relative;
  white-space: nowrap;
}
.sp-cupword::after {
  content: "";
  position: absolute;
  left: -2%;
  right: -2%;
  bottom: 6%;
  height: 0.18em;
  background: var(--volt);
  z-index: -1;
  transform: scaleX(0);
  transform-origin: left;
  animation: sp-swipe 0.8s var(--ease) 1s forwards;
}
@keyframes sp-swipe {
  to {
    transform: scaleX(1);
  }
}
.sp-hero-foot {
  /* Single left-hand column: the mascot is absolutely positioned over the right
     of the hero, so the copy and the form both stay clear of it. */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 26px;
  margin-top: 56px;
  max-width: 560px;
}
.sp-hero-copy {
  max-width: 460px;
}
.sp-hero-cta {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 560px;
  min-width: 0;
}
.sp-hero-cta .sp-capture {
  flex-direction: column;
  gap: 14px;
  max-width: none;
}
.sp-fields {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}
.sp-field {
  display: flex;
  flex-direction: column;
  gap: 9px;
  flex: 1 1 200px;
  min-width: 170px;
}
.sp-field-label {
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.sp-field-opt {
  text-transform: none;
  letter-spacing: 0.02em;
  opacity: 0.7;
}
/* Scoped under .sp-hero-cta so these outrank the older .sp-capture input and
   .sp-go rules further down the file. flex:none is required because
   .sp-capture input sets flex:1, which inside this column-flex .sp-field would
   make flex-basis govern height and collapse the field to its text height. */
.sp-hero-cta .sp-field input {
  flex: none;
  height: 56px;
  padding: 0 20px;
  font-family: var(--grotesk);
  font-size: 15px;
  line-height: 1;
  color: var(--ink);
  background: #fff;
  border: 1.5px solid var(--line);
  border-radius: 14px;
  outline: none;
  transition: border-color 0.2s;
}
.sp-hero-cta .sp-field input::placeholder {
  color: var(--ink-3);
}
.sp-hero-cta .sp-field input:focus {
  border-color: var(--blue);
}
.sp-hero-cta .sp-field--optional input {
  background: rgba(16, 24, 32, 0.045);
  border: 1.5px dashed var(--line);
}
.sp-hero-cta .sp-go--block {
  width: 100%;
  height: 56px;
  border-radius: 14px;
  font-size: 16px;
  justify-content: center;
}
.sp-hero-cta > .sp-referral {
  margin-top: 0;
  opacity: 0.8;
}
.sp-sub {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 0;
}
.sp-sub b {
  color: var(--ink);
}
.sp-referral {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
}
.sp-referral--center {
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 8px;
  max-width: 520px;
}
.sp-referral--after {
  margin-top: 20px;
  margin-bottom: 0;
  opacity: 0.75;
}
/* appearance:interaction-only still injects a ~72px placeholder, so the
   container stays collapsed and is opened only by .ts-interactive, which the
   helper toggles from Turnstile's before/after-interactive callbacks. */
.sp-turnstile {
  height: 0;
  overflow: hidden;
}
.sp-turnstile.ts-interactive {
  height: auto;
  overflow: visible;
  margin-top: 16px;
}
.sp-turnstile--center.ts-interactive {
  display: flex;
  justify-content: center;
}
.sp-referral-label {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  white-space: nowrap;
}
.sp-referral-input {
  height: 44px;
  padding: 0 18px;
  font-family: var(--grotesk);
  font-size: 14px;
  color: var(--ink);
  background: rgba(16, 24, 32, 0.035);
  border: 1.5px dashed var(--line);
  border-radius: 12px;
  outline: none;
  flex: 1;
  transition: border-color 0.2s;
}
.sp-referral-input::placeholder {
  color: var(--ink-3);
}
.sp-referral-input:focus {
  border-color: var(--blue);
}
.sp-capture {
  display: flex;
  gap: 10px;
  flex: 1;
  max-width: 520px;
  min-width: 300px;
}
.sp-capture--center {
  margin: 0 auto;
  justify-content: center;
}
.sp-capture input {
  flex: 1;
  height: 44px;
  line-height: 1;
  padding: 0 18px;
  font-family: var(--grotesk);
  font-size: 14px;
  color: var(--ink);
  background: #fff;
  border: 1.5px solid var(--line);
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
}
.sp-capture input::placeholder {
  color: var(--ink-3);
}
.sp-capture input:focus {
  border-color: var(--blue);
  background: #fff;
}
.sp-go {
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: var(--blue);
  color: #fff;
  font-family: var(--grotesk);
  font-weight: 800;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  transition: background 0.25s;
  position: relative;
}
.sp-go:hover {
  background: var(--blue-deep);
}
.sp-go--ok {
  background: #27ae60;
}
.sp-arr {
  transition: transform 0.3s var(--ease);
  display: inline-block;
}
.sp-go:hover .sp-arr {
  transform: translateX(5px);
}
@media (max-width: 520px) {
  .sp-capture {
    flex-direction: column;
    min-width: 0;
    width: 100%;
  }
  .sp-capture input,
  .sp-go {
    width: 100%;
  }
  .sp-go {
    justify-content: center;
  }
}

/* mascot + badge */
.sp-hero-art {
  position: absolute;
  right: 3vw;
  bottom: 0;
  width: clamp(230px, 30vw, 460px);
  pointer-events: none;
  z-index: 2;
}
.sp-hero-art img {
  width: 100%;
  display: block;
  filter: drop-shadow(0 30px 50px rgba(16, 24, 32, 0.18));
}
.sp-steam {
  position: absolute;
  left: 68%;
  top: 6%;
  width: 120px;
  height: 150px;
  pointer-events: none;
  z-index: 1;
  transform: translateX(-50%);
}
.sp-puff {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 35%,
    rgba(150, 165, 190, 0.7) 0%,
    rgba(140, 155, 180, 0.45) 45%,
    rgba(140, 155, 180, 0) 75%
  );
  opacity: 0;
  filter: blur(5px);
  transform: translate(-50%, 0) scale(0.4);
  animation: sp-steam-rise 4.2s ease-out infinite;
}
.sp-p1 {
  left: 48%;
  animation-delay: 0s;
}
.sp-p2 {
  left: 38%;
  animation-delay: 0.8s;
  width: 30px;
  height: 30px;
}
.sp-p3 {
  left: 58%;
  animation-delay: 1.6s;
  width: 42px;
  height: 42px;
}
.sp-p4 {
  left: 42%;
  animation-delay: 2.4s;
  width: 28px;
  height: 28px;
}
.sp-p5 {
  left: 54%;
  animation-delay: 3.2s;
  width: 34px;
  height: 34px;
}
@keyframes sp-steam-rise {
  0% {
    opacity: 0;
    transform: translate(-50%, 20px) scale(0.4) rotate(0deg);
    filter: blur(3px);
  }
  15% {
    opacity: 0.9;
  }
  60% {
    opacity: 0.7;
    transform: translate(calc(-50% - 14px), -90px) scale(1.1) rotate(-12deg);
    filter: blur(7px);
  }
  100% {
    opacity: 0;
    transform: translate(calc(-50% + 18px), -180px) scale(1.6) rotate(10deg);
    filter: blur(14px);
  }
}
@media (max-width: 1100px) {
  .sp-hero-art {
    opacity: 1;
  }
}
@media (max-width: 720px) {
  .sp-hero {
    padding: 110px 18px 0;
  }
  .sp-hero-art {
    display: none;
  }
  .sp-hero-foot {
    margin-top: 40px;
  }
}

/* ticker */
.sp-ticker {
  margin-top: 64px;
  margin-left: -32px;
  margin-right: -32px;
  width: calc(100% + 64px);
  border-top: 1.5px solid var(--ink);
  border-bottom: 1.5px solid var(--ink);
  background: var(--volt);
  overflow: hidden;
  white-space: nowrap;
  transform: rotate(-1deg) scale(1.01);
  transform-origin: center;
}
.sp-track {
  display: inline-flex;
  align-items: center;
  animation: sp-marquee 36s linear infinite;
  padding: 14px 0;
}
.sp-ticker:hover .sp-track {
  animation-play-state: paused;
}
.sp-item {
  display: inline-flex;
  align-items: center;
  gap: 26px;
  padding: 0 26px;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.sp-item::after {
  content: "☕";
  font-size: 13px;
}
@keyframes sp-marquee {
  to {
    transform: translateX(-50%);
  }
}
@media (max-width: 720px) {
  .sp-ticker {
    margin-left: -18px;
    margin-right: -18px;
    width: calc(100% + 36px);
  }
  .sp-track {
    padding: 10px 0;
  }
  .sp-item {
    gap: 16px;
    padding: 0 16px;
    font-size: 14px;
  }
}

/* latest issues */
.sp-latest {
  padding: 90px 0 70px;
}
.sp-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #27ae60;
  animation: sp-pulse 2s infinite;
  display: inline-block;
}
.sp-latest h2 {
  font-weight: 800;
  font-size: clamp(32px, 4.6vw, 56px);
  letter-spacing: -0.03em;
  line-height: 1.02;
  margin: 0 0 48px;
}
.sp-latest h2 em {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  color: var(--blue);
}
.sp-latest-rows {
  border-top: 1.5px solid var(--ink);
}
.sp-latest-row {
  display: grid;
  grid-template-columns: 90px 1fr auto;
  align-items: baseline;
  gap: 24px;
  padding: 26px 8px;
  border-bottom: 1px solid var(--line);
  text-decoration: none;
  color: inherit;
}
.sp-latest-date {
  font-family: var(--mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.sp-latest-title {
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.01em;
  transition: color 0.2s;
}
.sp-latest-row:hover .sp-latest-title {
  color: var(--blue);
}
.sp-latest-teaser {
  font-size: 14px;
  color: var(--ink-2);
  grid-column: 2;
  margin-top: -10px;
}
.sp-latest-arrow {
  grid-row: 1 / 3;
  grid-column: 3;
  align-self: center;
  font-size: 18px;
  transition: transform 0.3s var(--ease);
}
.sp-latest-row:hover .sp-latest-arrow {
  transform: translateX(5px);
  color: var(--blue);
}
.sp-skel {
  display: block;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--line) 25%, rgba(16, 24, 32, 0.06) 37%, var(--line) 63%);
  background-size: 400% 100%;
  animation: sp-shimmer 1.4s ease infinite;
}
.sp-skel--date {
  width: 50px;
  height: 12px;
}
.sp-skel--title {
  width: min(60%, 380px);
  height: 18px;
}
@keyframes sp-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
@media (max-width: 720px) {
  .sp-latest {
    padding: 56px 0 40px;
  }
  .sp-latest-row {
    grid-template-columns: 56px 1fr auto;
    gap: 14px;
    padding: 20px 0;
  }
  .sp-latest-teaser {
    display: none;
  }
  .sp-latest-title {
    font-size: 16px;
  }
}

/* shared */
.sp-wrap {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 32px;
}
@media (max-width: 720px) {
  .sp-wrap {
    padding: 0 18px;
  }
}
.sp-kicker {
  font-family: var(--mono);
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}
.sp-kicker::before {
  content: "";
  width: 44px;
  height: 1px;
  background: currentColor;
}
.sp-kicker--center {
  justify-content: center;
}

.sp-reveal {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity 0.9s var(--ease), transform 0.9s var(--ease);
}
.sp-reveal.sp-in {
  opacity: 1;
  transform: none;
}

/* anatomy */
.sp-anatomy {
  padding: 130px 0 90px;
}
.sp-anatomy h2 {
  font-weight: 800;
  font-size: clamp(36px, 5.4vw, 76px);
  letter-spacing: -0.035em;
  line-height: 1;
  margin: 0 0 64px;
  text-transform: uppercase;
}
.sp-anatomy h2 em {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  text-transform: none;
  color: var(--blue);
}
.sp-rows {
  border-top: 1.5px solid var(--ink);
}
.sp-rowitem {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  align-items: center;
  gap: 28px;
  padding: 34px 8px;
  border-bottom: 1.5px solid var(--ink);
  position: relative;
  overflow: hidden;
  cursor: default;
}
.sp-rowitem::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--ink);
  z-index: 0;
  transform: translateY(101%);
  transition: transform 0.45s var(--ease);
}
.sp-rowitem:hover::before {
  transform: translateY(0);
}
.sp-rowitem > * {
  position: relative;
  z-index: 1;
  transition: color 0.35s;
}
.sp-num {
  font-family: var(--mono);
  font-size: 14px;
  color: var(--ink-3);
}
.sp-name {
  font-weight: 800;
  font-size: clamp(24px, 3.4vw, 44px);
  letter-spacing: -0.03em;
  text-transform: uppercase;
}
.sp-desc {
  font-size: 15px;
  color: var(--ink-2);
  max-width: 340px;
  justify-self: end;
  text-align: right;
}
.sp-rowitem:hover .sp-num {
  color: var(--volt);
}
.sp-rowitem:hover .sp-name {
  color: var(--paper);
}
.sp-rowitem:hover .sp-desc {
  color: rgba(250, 247, 240, 0.75);
}
@media (max-width: 820px) {
  .sp-rowitem {
    grid-template-columns: 56px 1fr;
    gap: 14px;
  }
  .sp-desc {
    display: none;
  }
}
@media (max-width: 720px) {
  .sp-anatomy {
    padding: 64px 0 48px;
  }
  .sp-anatomy h2 {
    margin-bottom: 40px;
  }
}

/* stats */
.sp-stats {
  padding: 110px 0;
  background: var(--paper-2);
}
.sp-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  background: var(--line);
  border: 1.5px solid var(--ink);
}
.sp-cell {
  background: var(--paper-2);
  padding: 44px 30px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sp-v {
  font-family: var(--serif);
  font-size: clamp(44px, 5.5vw, 84px);
  line-height: 1;
  letter-spacing: -0.02em;
}
.sp-v >>> b {
  color: var(--blue);
  font-weight: 400;
}
.sp-k {
  font-family: var(--mono);
  font-size: 11.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-3);
}
@media (max-width: 820px) {
  .sp-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 720px) {
  .sp-stats {
    padding: 64px 0;
  }
}

/* quote (dark) */
.sp-dark {
  background: var(--ink);
  color: var(--paper);
  padding: 140px 0;
}
.sp-dark .sp-kicker {
  color: rgba(250, 247, 240, 0.5);
}
.sp-bigquote {
  font-family: var(--serif);
  font-weight: 400;
  font-size: clamp(30px, 4.6vw, 62px);
  line-height: 1.18;
  letter-spacing: -0.01em;
  margin: 0;
  max-width: 1000px;
}
.sp-bigquote >>> em {
  font-style: italic;
  color: var(--volt);
}
.sp-attrib {
  margin-top: 36px;
  font-family: var(--mono);
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(250, 247, 240, 0.55);
}
.sp-quote-nav {
  display: flex;
  gap: 10px;
  margin-top: 44px;
}
.sp-quote-nav button {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid rgba(250, 247, 240, 0.35);
  background: transparent;
  color: var(--paper);
  cursor: pointer;
  font-size: 17px;
  transition: background 0.25s, border-color 0.25s, transform 0.2s;
}
.sp-quote-nav button:hover {
  background: var(--volt);
  border-color: var(--volt);
  color: var(--ink);
  transform: scale(1.06);
}
.sp-quote-body {
  transition: opacity 0.35s var(--ease), transform 0.35s var(--ease);
}
.sp-swap {
  opacity: 0;
  transform: translateY(14px);
}
@media (max-width: 720px) {
  .sp-dark {
    padding: 72px 0;
  }
}

/* final CTA */
.sp-final {
  padding: 150px 0 60px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.sp-sun {
  position: absolute;
  left: 50%;
  bottom: -32vw;
  transform: translateX(-50%);
  width: 64vw;
  height: 64vw;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 30%, var(--sky) 0%, rgba(227, 242, 253, 0) 70%);
  pointer-events: none;
}
.sp-final h2 {
  font-weight: 800;
  font-size: clamp(44px, 8vw, 120px);
  line-height: 0.95;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  margin: 0 0 18px;
  position: relative;
}
.sp-final h2 em {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  text-transform: none;
  color: var(--blue);
}
.sp-final p {
  font-size: 17px;
  color: var(--ink-2);
  margin: 0 0 44px;
  position: relative;
}
.sp-final .sp-capture {
  margin: 0 auto;
  position: relative;
}
.sp-note {
  margin-top: 18px;
  font-family: var(--mono);
  font-size: 11.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  position: relative;
}

@media (max-width: 720px) {
  .sp-final {
    padding: 90px 0 40px;
  }
  footer {
    margin-top: 64px;
  }
}

footer {
  margin-top: 130px;
  border-top: 1.5px solid var(--ink);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 26px 32px;
  font-family: var(--mono);
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  flex-wrap: wrap;
  position: relative;
  background: var(--paper);
}
footer a {
  color: var(--ink);
  text-decoration: none;
  cursor: pointer;
}
footer a:hover {
  color: var(--blue);
}

/* success modal */
.sp-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(16, 24, 32, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.sp-modal {
  width: 100%;
  max-width: 440px;
  background: var(--paper);
  border: 1.5px solid var(--line);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 30px 70px rgba(16, 24, 32, 0.3);
}
.sp-modal-kicker {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 16px;
}
.sp-modal-title {
  font-weight: 800;
  font-size: clamp(26px, 4vw, 34px);
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 0 0 14px;
}
.sp-modal-title em {
  font-family: var(--serif);
  font-style: italic;
  font-weight: 400;
  color: var(--blue);
}
.sp-modal-text {
  font-size: 16px;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 0 0 28px;
}
.sp-modal-close {
  width: 100%;
  justify-content: center;
  height: 48px;
  font-size: 14px;
}
.sp-modal-fade-enter-active,
.sp-modal-fade-leave-active {
  transition: opacity 0.25s var(--ease);
}
.sp-modal-fade-enter-active .sp-modal,
.sp-modal-fade-leave-active .sp-modal {
  transition: transform 0.25s var(--ease), opacity 0.25s var(--ease);
}
.sp-modal-fade-enter,
.sp-modal-fade-leave-to {
  opacity: 0;
}
.sp-modal-fade-enter .sp-modal,
.sp-modal-fade-leave-to .sp-modal {
  transform: translateY(16px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .sp-row > span {
    animation: none;
    transform: none;
  }
  .sp-cupword::after {
    animation: none;
    transform: scaleX(1);
  }
  .sp-track {
    animation: none;
  }
  .sp-puff {
    animation: none;
    opacity: 0;
  }
  .sp-reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
